import { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';

import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { Body, SubHeading } from '@/components/ui/Text';
import { setPendingNavigationIntent } from '@/navigation/pendingNavigationIntent';
import { acceptInviteLink } from '@/services/collaboration';
import { useTheme } from '@/theme';
import { usePremiumGateStore } from '@/state/premiumGateStore';
import { useUserStore } from '@/state/userStore';

function normalizeLinkId(raw: string | string[] | undefined): string | undefined {
  if (!raw) {
    return undefined;
  }
  const value = Array.isArray(raw) ? raw[0] : raw;
  const trimmed = String(value ?? '').trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Shareable trip invite links use `/invite/{linkId}` (web) and `tripvio://invite/{linkId}` (native).
 * This route must exist or Expo Router shows “Unmatched Route” when the link is opened.
 */
export default function TripInviteLinkScreen() {
  const theme = useTheme();
  const router = useRouter();
  const openPremiumGate = usePremiumGateStore((s) => s.open);
  const params = useLocalSearchParams<{ linkId?: string | string[] }>();
  const linkId = normalizeLinkId(params.linkId);

  const authReady = useUserStore((s) => s.authReady);
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const needsEmailVerification = useUserStore((s) => s.needsEmailVerification);
  const hasCompletedOnboarding = useUserStore((s) => s.hasCompletedOnboarding);
  const firebaseUser = useUserStore((s) => s.firebaseUser);
  const profileName = useUserStore((s) => s.profile?.name);

  const [acceptError, setAcceptError] = useState<string | undefined>();
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (
      !linkId ||
      !authReady ||
      !isAuthenticated ||
      !firebaseUser?.email ||
      !firebaseUser?.uid ||
      needsEmailVerification ||
      !hasCompletedOnboarding
    ) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        setAcceptError(undefined);
        const inviteDisplayName = profileName?.trim() || undefined;
        const result = await acceptInviteLink(linkId, firebaseUser.uid, firebaseUser.email!, inviteDisplayName);

        if (cancelled) {
          return;
        }

        if (!result.success) {
          const isTripLimit = (result.error || '').toLowerCase().includes('free plan allows 1 active trip');
          if (isTripLimit) {
            openPremiumGate({
              feature: 'trips',
              title: 'Upgrade to Join Another Active Trip',
              message:
                'Free users can only be on one active trip at a time. Upgrade to Premium for unlimited active trips.',
              cta: 'upgrade_now',
            });
            return;
          }
          setAcceptError(result.error || 'Unable to accept this invite link.');
          return;
        }

        if (result.tripId) {
          router.replace({
            pathname: '/(tabs)/plan',
            params: { tripId: result.tripId },
          });
        } else {
          router.replace('/(tabs)/plan');
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        const message =
          error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : 'Something went wrong while accepting this invite.';
        setAcceptError(message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    authReady,
    firebaseUser?.email,
    firebaseUser?.uid,
    hasCompletedOnboarding,
    isAuthenticated,
    linkId,
    needsEmailVerification,
    openPremiumGate,
    profileName,
    retryNonce,
    router,
  ]);

  const handleContinueToSignIn = () => {
    if (!linkId) {
      return;
    }
    setPendingNavigationIntent({ kind: 'invite', linkId });
    router.replace('/(onboarding)/sign-in');
  };

  const handleBackHome = () => {
    router.replace('/');
  };

  const handleRetry = () => {
    setAcceptError(undefined);
    setRetryNonce((n) => n + 1);
  };

  const handleOpenVerifyEmail = () => {
    router.replace('/(onboarding)/verify-email');
  };

  if (!linkId) {
    return (
      <Screen title="Invite link" showOfflineIndicator={false} showBackButton onBack={() => router.back()}>
        <SubHeading>Invalid link</SubHeading>
        <Body>This invite link is missing an ID. Ask the trip owner to generate a new link.</Body>
      </Screen>
    );
  }

  if (!authReady) {
    return (
      <Screen title="Invite link" scrollable={false} showOfflineIndicator={false}>
        <View style={{ alignItems: 'center', justifyContent: 'center', flex: 1, paddingVertical: theme.spacing(3) }}>
          <ActivityIndicator size="large" color={theme.colors.primary} accessibilityLabel="Loading" />
          <Body style={{ marginTop: theme.spacing(2), opacity: 0.85 }}>Loading Tripvio…</Body>
        </View>
      </Screen>
    );
  }

  if (!isAuthenticated || !firebaseUser?.email) {
    return (
      <Screen title="Trip invite" showOfflineIndicator={false} scrollable={false}>
        <View style={{ gap: theme.spacing(2) }}>
          <SubHeading>You’ve been invited</SubHeading>
          <Body style={{ opacity: 0.85 }}>
            Sign up or sign in to Tripvio. After you’re signed in (and your email is verified, if required), we’ll add
            you to the trip from this link.
          </Body>
          <Button label="Continue" onPress={handleContinueToSignIn} />
          <Button label="Back to welcome" variant="ghost" onPress={handleBackHome} />
        </View>
      </Screen>
    );
  }

  if (needsEmailVerification) {
    return (
      <Screen title="Trip invite" showOfflineIndicator={false} scrollable={false}>
        <View style={{ gap: theme.spacing(2) }}>
          <SubHeading>Verify your email</SubHeading>
          <Body style={{ opacity: 0.85 }}>
            Verify the email on your Tripvio account to accept this invite link.
          </Body>
          <Button label="Open email verification" onPress={handleOpenVerifyEmail} />
        </View>
      </Screen>
    );
  }

  if (!hasCompletedOnboarding) {
    return (
      <Screen title="Trip invite" showOfflineIndicator={false} scrollable={false}>
        <View style={{ gap: theme.spacing(2) }}>
          <SubHeading>Finish setup</SubHeading>
          <Body style={{ opacity: 0.85 }}>
            Finish onboarding on Tripvio, then we’ll take you back to this invite automatically if you return here from
            your clipboard or email.
          </Body>
          <Button label="Continue setup" onPress={() => router.replace('/')} />
        </View>
      </Screen>
    );
  }

  if (acceptError) {
    return (
      <Screen title="Trip invite" showOfflineIndicator={false}>
        <SubHeading>Couldn’t join the trip</SubHeading>
        <Body>{acceptError}</Body>
        <View style={{ marginTop: theme.spacing(2), gap: theme.spacing(1) }}>
          <Button label="Try again" onPress={handleRetry} />
          <Button label="Back to Tripvio" variant="ghost" onPress={() => router.replace('/(tabs)')} />
        </View>
      </Screen>
    );
  }

  return (
    <Screen title="Trip invite" scrollable={false} showOfflineIndicator={false}>
      <View style={{ gap: theme.spacing(2), alignItems: 'center', paddingVertical: theme.spacing(3) }}>
        <ActivityIndicator size="large" color={theme.colors.primary} accessibilityLabel="Joining trip" />
        <Body style={{ opacity: 0.85 }}>Joining the trip…</Body>
      </View>
    </Screen>
  );
}
